import { useMemo } from "react";
import PhoneInput, {
  isValidPhoneNumber,
  getCountryCallingCode,
  parsePhoneNumber,
} from "react-phone-number-input";
import { getCountries, getExampleNumber } from "libphonenumber-js";
import examples from "libphonenumber-js/examples.mobile.json";
import labels from "react-phone-number-input/locale/en.json";
import "react-phone-number-input/style.css";

// Build a reverse lookup once: English country name → ISO2 code.
// We add a few aliases for names the backend stores differently than
// the canonical react-phone-number-input labels.
const NAME_TO_ISO = (() => {
  const out = {};
  for (const iso of getCountries()) {
    const label = labels[iso];
    if (label) out[label.toLowerCase()] = iso;
  }
  // Aliases used in /app/backend/constants.py that differ from the
  // libphonenumber English labels.
  const aliases = {
    "united states": "US",
    "united kingdom": "GB",
    "ivory coast": "CI",
    "czech republic": "CZ",
    "congo (brazzaville)": "CG",
    "congo (kinshasa)": "CD",
    "north korea": "KP",
    "south korea": "KR",
    "north macedonia": "MK",
    "myanmar": "MM",
    "cape verde": "CV",
    "brunei": "BN",
    "syria": "SY",
    "iran": "IR",
    "laos": "LA",
    "russia": "RU",
    "vietnam": "VN",
    "taiwan": "TW",
    "moldova": "MD",
    "vatican city": "VA",
    "palestine": "PS",
    "eswatini": "SZ",
    "timor-leste": "TL",
    "bolivia": "BO",
    "tanzania": "TZ",
    "venezuela": "VE",
    "micronesia": "FM",
  };
  for (const [k, v] of Object.entries(aliases)) out[k] = v;
  return out;
})();

export function countryNameToIso2(name) {
  if (!name) return undefined;
  return NAME_TO_ISO[String(name).trim().toLowerCase()];
}

/**
 * PhoneInputField — country-flag dropdown + E.164 phone input.
 *
 * Per admin spec, validation is intentionally LENIENT: the field
 * surfaces a per-country example placeholder (e.g. "+1 702 123 4567"
 * for the United States, "+44 1632 960123" for the United Kingdom) so
 * agents can VISUALIZE the expected shape, but no submit-block is
 * enforced — submission accepts any phone string the user types.
 *
 * Props:
 *   value          string  — E.164 number (e.g. "+17021234567") or ""
 *   onChange       (val)   — receives E.164 string or undefined
 *   countryName    string  — full English country name from the Location
 *                            field. When provided AND the input is empty,
 *                            we seed the dropdown with that country's
 *                            dialing code so the agent doesn't have to
 *                            re-pick.
 *   defaultIso2    string  — fallback ISO2 when no country selected (US)
 *   disabled       bool
 *   testid         string  — base data-testid prefix
 */
export default function PhoneInputField({
  value,
  onChange,
  countryName,
  defaultIso2 = "US",
  disabled,
  testid = "field-work_phone",
}) {
  // Resolve the ISO2 to use as the dropdown's default. Priority:
  //   1) If the user has typed a number, libphonenumber will infer the
  //      country from its prefix → don't override.
  //   2) Else if the Location → Country field is set, use that ISO.
  //   3) Else fall back to defaultIso2 (US).
  const inferredIso = useMemo(() => {
    if (value) {
      try {
        const parsed = parsePhoneNumber(value);
        if (parsed?.country) return parsed.country;
      } catch { /* number too partial — fall through */ }
    }
    return countryNameToIso2(countryName) || defaultIso2;
  }, [value, countryName, defaultIso2]);

  // Dynamic placeholder. libphonenumber's example formatted as
  // international (e.g. "+1 702 123 4567"). If we don't have a sample
  // for the chosen country, fall back to a generic.
  const placeholder = useMemo(() => {
    try {
      const ex = getExampleNumber(inferredIso, examples);
      if (ex) return `e.g. ${ex.formatInternational()}`;
    } catch { /* fall through */ }
    return "e.g. +1 702 123 4567";
  }, [inferredIso]);

  // NOTE: Per admin spec, no inline "invalid format" error is shown —
  // the placeholder + country flag dropdown are pure visual guidance.

  return (
    <div data-testid={`${testid}-wrap`} className="phone-input-shell">
      <PhoneInput
        international
        defaultCountry={inferredIso}
        // `value` should be E.164 (e.g. "+17021234567"). When the parent
        // form hasn't loaded one yet, pass undefined so the component
        // shows the placeholder.
        value={value || undefined}
        onChange={(v) => onChange(v || "")}
        disabled={disabled}
        placeholder={placeholder}
        data-testid={testid}
        countryCallingCodeEditable={false}
        // ARIA + a stable testid hook for the inner <input>.
        numberInputProps={{
          "data-testid": `${testid}-input`,
          className: "phone-input-number",
          autoComplete: "tel",
        }}
        countrySelectProps={{
          "data-testid": `${testid}-country`,
          "aria-label": "Country dialing code",
        }}
      />
    </div>
  );
}

// Re-export so callers (LeadForm submit guard) don't have to import
// libphonenumber-js directly.
export { isValidPhoneNumber, getCountryCallingCode };
